import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  Clock, 
  MapPin, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Loader2,
  Users,
  UserCheck,
  UserX,
  Search,
  RefreshCw,
  BarChart3,
  Building2,
  ChevronDown,
  ChevronRight,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";

const API_URL = '';

export default function CoordinatorTeacherAttendance() {
  // Initialize user from localStorage immediately to avoid race condition
  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem("user") || localStorage.getItem("currentUser");
    return userData ? JSON.parse(userData) : null;
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState({ overall_stats: {}, schools: [] });
  const [reportData, setReportData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [activeTab, setActiveTab] = useState("today");
  const [expandedSchools, setExpandedSchools] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [userLoaded, setUserLoaded] = useState(false);

  // Ensure user is properly loaded before making API calls
  useEffect(() => {
    const userData = localStorage.getItem("user") || localStorage.getItem("currentUser");
    if (userData) {
      setUser(JSON.parse(userData));
    }
    setUserLoaded(true);
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => {
      clearInterval(timer);
    };
  }, []);

  // Fetch attendance only after user is loaded
  useEffect(() => {
    if (!userLoaded) return;
    
    fetchTodayAttendance();
    
    // Auto-refresh every 60 seconds
    const refreshTimer = setInterval(() => {
      fetchTodayAttendance(true);
    }, 60000);
    
    return () => {
      clearInterval(refreshTimer);
    };
  }, [userLoaded]);

  useEffect(() => {
    if (activeTab === "report" && userLoaded) {
      fetchMonthlyReport();
    }
  }, [activeTab, userLoaded]);

  const getSchoolType = () => {
    if (!user) return "secondary";
    return user.department === "Primary Education" ? "primary" : "secondary";
  };

  const fetchTodayAttendance = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem("authToken");
      const schoolType = getSchoolType();
      const response = await fetch(`${API_URL}/api/teacher-attendance/coordinator/today?school_type=${schoolType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      
      if (response.ok) {
        setData(result);
        // Auto-expand all schools
        const expanded = {};
        result.schools?.forEach(s => { expanded[s.school_id] = true; });
        setExpandedSchools(expanded);
      } else {
        toast.error(result.detail || "Failed to load attendance data");
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      if (!silent) toast.error("Failed to load attendance data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchMonthlyReport = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const schoolType = getSchoolType();
      const response = await fetch(`${API_URL}/api/teacher-attendance/coordinator/report?school_type=${schoolType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json();
      if (response.ok) {
        setReportData(result);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "--:--";
    const date = new Date(isoString);
    const vanuatuTime = new Date(date.getTime() + (11 * 60 * 60 * 1000));
    return vanuatuTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getLocationIcon = (locationStatus, distance, isOnSchoolNetwork) => {
    if (isOnSchoolNetwork) {
      return <Badge className="bg-green-100 text-green-800 text-xs" data-testid="location-on-site-network"><MapPin className="w-3 h-3 mr-1" /> On-Site (Network)</Badge>;
    }
    switch (locationStatus) {
      case "at_school":
        return <Badge className="bg-green-100 text-green-800 text-xs"><MapPin className="w-3 h-3 mr-1" /> At School</Badge>;
      case "nearby":
        return <Badge className="bg-yellow-100 text-yellow-800 text-xs"><MapPin className="w-3 h-3 mr-1" /> {distance}m</Badge>;
      case "off_campus":
        return <Badge className="bg-red-100 text-red-800 text-xs"><AlertTriangle className="w-3 h-3 mr-1" /> {distance ? `${(distance/1000).toFixed(1)}km` : 'Off Campus'}</Badge>;
      default:
        return null;
    }
  };

  const getStatusBadge = (teacher) => {
    if (teacher.verification_status === "absent") {
      return <Badge className="bg-gray-100 text-gray-800">Absent</Badge>;
    }
    if (!teacher.sign_in_time) {
      return <Badge className="bg-red-100 text-red-800">Not Signed In</Badge>;
    }
    if (teacher.verification_status === "verified") {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Verified</Badge>;
    }
    if (teacher.verification_status === "rejected") {
      return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" /> Rejected</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
  };

  const toggleSchool = (schoolId) => {
    setExpandedSchools(prev => ({
      ...prev,
      [schoolId]: !prev[schoolId]
    }));
  };

  // Filter teachers across all schools
  const filterTeachers = (teachers) => {
    return teachers.filter(teacher => {
      const matchesSearch = teacher.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterStatus === "all") return matchesSearch;
      if (filterStatus === "signed_in") return matchesSearch && teacher.sign_in_time;
      if (filterStatus === "not_signed_in") return matchesSearch && !teacher.sign_in_time;
      if (filterStatus === "late") return matchesSearch && teacher.is_late;
      if (filterStatus === "off_campus") return matchesSearch && teacher.location_status === "off_campus" && !teacher.is_on_school_network;
      
      return matchesSearch;
    });
  };

  // Get Vanuatu time
  const vanuatuTime = new Date(currentTime.getTime() + (11 * 60 * 60 * 1000));
  const timeString = vanuatuTime.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });

  const schoolTypeLabel = user?.department === "Primary Education" ? "Primary Schools" : "Secondary Schools";

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="coordinator-teacher-attendance">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 shadow-xl">
        <div>
          <h1 className="text-2xl font-bold text-white">Teacher Attendance Overview</h1>
          <p className="text-slate-300">
            {schoolTypeLabel} &bull; {data.date} &bull; Current Time: <span className="text-blue-300 font-semibold">{timeString}</span>
          </p>
        </div>
        <Button 
          onClick={() => fetchTodayAttendance()}
          disabled={refreshing}
          className="bg-blue-500 hover:bg-blue-600 text-white"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="today">Today's Attendance</TabsTrigger>
          <TabsTrigger value="report">Monthly Report</TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="space-y-6">
          {/* Overall Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 border-0 shadow-lg shadow-blue-500/15">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-white/80" />
                  <span className="text-sm text-blue-100">Total</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{data.overall_stats?.total || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 shadow-lg shadow-emerald-500/15">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-white/80" />
                  <span className="text-sm text-emerald-100">Signed In</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{data.overall_stats?.signed_in || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-500 to-red-600 border-0 shadow-lg shadow-red-500/15">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <UserX className="w-5 h-5 text-white/80" />
                  <span className="text-sm text-red-100">Not Signed In</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{data.overall_stats?.not_signed_in || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500 to-orange-500 border-0 shadow-lg shadow-amber-500/15">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-white/80" />
                  <span className="text-sm text-amber-100">Late</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{data.overall_stats?.late || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-violet-500 to-purple-600 border-0 shadow-lg shadow-violet-500/15">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-white/80" />
                  <span className="text-sm text-violet-100">At School</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{data.overall_stats?.at_school || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-600 to-slate-700 border-0 shadow-lg shadow-slate-500/15">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-white/80" />
                  <span className="text-sm text-slate-200">Schools</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{data.schools?.length || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search teachers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "all", label: "All" },
                { value: "signed_in", label: "Signed In" },
                { value: "not_signed_in", label: "Not Signed In" },
                { value: "late", label: "Late" },
                { value: "off_campus", label: "Off Campus" }
              ].map(filter => (
                <Button
                  key={filter.value}
                  variant={filterStatus === filter.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterStatus(filter.value)}
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Schools List */}
          <div className="space-y-4">
            {data.schools?.map((school) => {
              const filteredTeachers = filterTeachers(school.teachers);
              const isExpanded = expandedSchools[school.school_id];
              
              return (
                <Card key={school.school_id}>
                  <Collapsible open={isExpanded} onOpenChange={() => toggleSchool(school.school_id)}>
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="cursor-pointer hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-500" />
                            )}
                            <div className="text-left">
                              <CardTitle className="text-lg">{school.school_name}</CardTitle>
                              <CardDescription>
                                {school.school_type === "junior_secondary" ? "Junior Secondary" : 
                                 school.school_type === "senior_secondary" ? "Senior Secondary" : "Primary"} • 
                                {school.stats.total} teachers
                              </CardDescription>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Badge className="bg-green-100 text-green-800">
                              {school.stats.signed_in} in
                            </Badge>
                            <Badge className="bg-red-100 text-red-800">
                              {school.stats.not_signed_in} out
                            </Badge>
                            {school.stats.late > 0 && (
                              <Badge className="bg-orange-100 text-orange-800">
                                {school.stats.late} late
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <CardContent>
                        {filteredTeachers.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="border-b bg-gray-50">
                                  <th className="text-left p-3 font-medium">Teacher</th>
                                  <th className="text-left p-3 font-medium">Sign In</th>
                                  <th className="text-left p-3 font-medium">Location</th>
                                  <th className="text-left p-3 font-medium">Status</th>
                                  <th className="text-left p-3 font-medium">Sign Out</th>
                                  <th className="text-left p-3 font-medium">Hours</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredTeachers.map((teacher) => (
                                  <tr key={teacher.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3">
                                      <div>
                                        <p className="font-medium">{teacher.name}</p>
                                        <p className="text-sm text-gray-500">{teacher.department || "Teacher"}</p>
                                      </div>
                                    </td>
                                    <td className="p-3">
                                      <span className={teacher.is_late ? "text-orange-600 font-medium" : ""}>
                                        {formatTime(teacher.sign_in_time)}
                                      </span>
                                      {teacher.is_late && (
                                        <Badge className="ml-2 bg-orange-100 text-orange-800 text-xs">Late</Badge>
                                      )}
                                      {teacher.is_late && teacher.notes && teacher.notes.startsWith('Late reason:') && (
                                        <span className="ml-2 text-xs text-orange-600 italic">{teacher.notes.replace('Late reason: ', '')}</span>
                                      )}
                                    </td>
                                    <td className="p-3">
                                      {getLocationIcon(teacher.location_status, teacher.distance_meters, teacher.is_on_school_network)}
                                    </td>
                                    <td className="p-3">
                                      {getStatusBadge(teacher)}
                                      {teacher.verification_status === "absent" && teacher.absence_reason && (
                                        <p className="mt-1 text-xs text-gray-500 italic" data-testid={`coord-absence-reason-${teacher.id}`}>{teacher.absence_reason}</p>
                                      )}
                                      {teacher.admin_comment && teacher.admin_comment !== teacher.absence_reason && (
                                        <p className="mt-1 text-xs text-blue-700 flex items-start gap-1 max-w-[240px]" data-testid={`coord-admin-comment-${teacher.id}`}>
                                          <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                                          <span>{teacher.admin_comment}{teacher.admin_comment_by ? <span className="text-gray-400"> — {teacher.admin_comment_by}</span> : null}</span>
                                        </p>
                                      )}
                                    </td>
                                    <td className="p-3">
                                      {formatTime(teacher.sign_out_time)}
                                    </td>
                                    <td className="p-3">
                                      {teacher.hours_worked ? `${teacher.hours_worked}h` : "--"}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-500">
                            No teachers matching your criteria
                          </div>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              );
            })}

            {data.schools?.length === 0 && (
              <Card>
                <CardContent className="text-center py-8 text-gray-500">
                  No schools found in your jurisdiction
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="report" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Monthly Attendance Report
              </CardTitle>
              <CardDescription>
                {reportData ? `${new Date(0, reportData.month - 1).toLocaleString('default', { month: 'long' })} ${reportData.year}` : 'Loading...'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {reportData?.schools?.length > 0 ? (
                <div className="space-y-6">
                  {reportData.schools.map((school) => (
                    <div key={school.school_id} className="border rounded-lg p-4">
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        <Building2 className="w-5 h-5" />
                        {school.school_name}
                        <Badge variant="outline" className="ml-2">
                          {school.school_type === "junior_secondary" ? "Junior Secondary" : 
                           school.school_type === "senior_secondary" ? "Senior Secondary" : "Primary"}
                        </Badge>
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-gray-50">
                              <th className="text-left p-3 font-medium">Teacher</th>
                              <th className="text-center p-3 font-medium">Days Present</th>
                              <th className="text-center p-3 font-medium">Days Absent</th>
                              <th className="text-center p-3 font-medium">Days Late</th>
                              <th className="text-center p-3 font-medium">Avg Hours</th>
                              <th className="text-center p-3 font-medium">Off-Campus</th>
                            </tr>
                          </thead>
                          <tbody>
                            {school.teachers.map((teacher) => (
                              <tr key={teacher.id} className="border-b hover:bg-gray-50">
                                <td className="p-3">
                                  <p className="font-medium">{teacher.name}</p>
                                  <p className="text-sm text-gray-500">{teacher.department || "Teacher"}</p>
                                </td>
                                <td className="p-3 text-center">
                                  <span className="text-green-600 font-medium">{teacher.days_present}</span>
                                </td>
                                <td className="p-3 text-center">
                                  <span className={teacher.days_absent > 3 ? "text-red-600 font-medium" : "text-gray-600"}>
                                    {teacher.days_absent}
                                  </span>
                                </td>
                                <td className="p-3 text-center">
                                  <span className={teacher.days_late > 3 ? "text-orange-600 font-medium" : "text-gray-600"}>
                                    {teacher.days_late}
                                  </span>
                                </td>
                                <td className="p-3 text-center">{teacher.avg_hours}h</td>
                                <td className="p-3 text-center">
                                  {teacher.off_campus_signins > 0 && (
                                    <Badge className="bg-yellow-100 text-yellow-800">{teacher.off_campus_signins}</Badge>
                                  )}
                                  {teacher.off_campus_signins === 0 && <span className="text-gray-400">0</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No attendance data available for this month
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
